import { useState } from 'react';

interface ElementCardProps {
  symbol: string;
  number: string;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  /** Frosted glass treatment (design handoff default). */
  glass?: boolean;
}

const sizes = {
  sm: { box: 64, sym: 22, num: 10, lab: 9, pad: 8 },
  md: { box: 88, sym: 30, num: 11, lab: 10, pad: 12 },
  lg: { box: 132, sym: 48, num: 12, lab: 11, pad: 16 },
};

export function ElementCard({
  symbol,
  number,
  label,
  size = 'md',
  interactive = false,
  glass = true,
}: ElementCardProps) {
  const s = sizes[size];
  const [hover, setHover] = useState(false);
  const labelFontSize = size === 'lg' ? 9 : 8;

  return (
    <div
      onMouseEnter={() => interactive && setHover(true)}
      onMouseLeave={() => interactive && setHover(false)}
      style={{
        width: s.box,
        height: s.box,
        padding: s.pad,
        background: glass
          ? 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.05) 100%)'
          : 'var(--bg-surface-elevated)',
        backdropFilter: glass ? 'blur(10px) saturate(140%)' : 'none',
        WebkitBackdropFilter: glass ? 'blur(10px) saturate(140%)' : 'none',
        border: `1px solid ${hover ? 'var(--accent)' : 'rgba(255,255,255,0.10)'}`,
        borderRadius: 4,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition:
          'transform 240ms var(--ease-physical), border-color 240ms var(--ease-out), box-shadow 240ms var(--ease-out)',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover
          ? '0 8px 32px var(--radium-glow), 0 0 0 1px var(--radium-glow)'
          : glass
            ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 20px -8px rgba(0,0,0,0.4)'
            : 'none',
        cursor: interactive ? 'default' : 'inherit',
        flexShrink: 0,
      }}
    >
      <div
        className="text-mono"
        style={{
          fontSize: s.num,
          color: 'var(--ink-muted)',
          letterSpacing: '0.04em',
          fontWeight: 500,
        }}
      >
        {number}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: s.sym,
          fontWeight: 600,
          color: 'var(--ink-primary)',
          letterSpacing: '-0.01em',
          lineHeight: 1,
        }}
      >
        {symbol}
      </div>
      <div
        className="text-mono"
        style={{
          fontSize: labelFontSize,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: 'var(--ink-muted)',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          textAlign: 'left',
        }}
      >
        {label}
      </div>
    </div>
  );
}
